import type {
  LogItemType,
  LogAttributesType,
  Change,
} from "@/types/common/logs";
import { FORM_ASSIGNMENTS_STATUS } from "@/types/entities/formality-request";
import type { ActionColors, Status } from "./logs.types";

export function dateToUtc(input: Date | string): string {
  const date = new Date(input);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000000Z`;
}

export const getChangeTitle = (
  changeName: string,
  subject_type?: string,
  defaultTile: string = "Cambio de proceso",
  entity: string = "proceso",
): string => {
  let titles: Record<string, string> = {
    name: `Título de ${entity} actualizado`,
    description: `Descripción de ${entity} actualizada`,
    file_name: "Ficha técnica actualizada",
    status: `Estatus de ${entity} actualizado`,
    tags: `Etiqueta de ${entity} actualizada`,
    // Tag-specific changes
    tag_name: "Nombre de etiqueta actualizado",
    tag_description: "Descripción de etiqueta actualizada",
    tag_status: "Estado de etiqueta actualizado",
    tag_image: "Imagen de etiqueta actualizada",
    permissions: "Permisos del rol actualizados",
  };
  if (subject_type === "expedient_base_entity") {
    titles = {
      name: "Nombre de entidad actualizado",
      description: "Descripción de entidad actualizada",
      status: "Estatus de entidad actualizado",
      tags: "Etiqueta de entidad actualizada",
      // Tag-specific changes
    };
  }
  if (subject_type === "expedient_base_registry") {
    titles = {
      name: "Nombre del expediente actualizado",
      description: "Descripción del expediente actualizada",
      status: "Estado del expediente actualizado",
      // Specific descriptions for associate changes
      users_linked: "Usuario asignado al expediente",
      associate_added: "Usuario asignado al expediente",
      owners_linked: "Usuario asignado al expediente",
      associate_updated: "Usuario actualizado en el expediente",
      users_unlinked: "Usuario desasignado del expediente",
      owners_unlinked: "Usuario desasignado del expediente",
      associate_removed: "Usuario desasignado del expediente",
      associate_deleted: "Usuario desasignado del expediente",
    };
  }
  return titles[changeName] || defaultTile;
};

export const actionTranslator = (action: string) => {
  if (!action) {
    return;
  }
  if (action === "created") {
    return "creado";
  }
  if (action === "updated") {
    return "actualizado";
  }
  if (action === "deleted") {
    return "desactivado";
  }
  if (action === "restored") {
    return "reactivado";
  }
  if (action === "activated") {
    return "activado";
  }
  if (action === "deactivated") {
    return "desactivado";
  }
  if (action === "process_duplicated") {
    return "duplicado";
  }
  if (action === "users_assigned") {
    return "Usuarios asignados";
  }
  if (action === "users_unassigned") {
    return "Usuarios desasignados";
  }
  if (action === "permissions_sync" || action === "permissions_updated") {
    return "Permisos actualizados";
  }
};

export const logTypeTranslator = (type: string) => {
  if (!type) {
    return;
  }
  if (type === "process") {
    return "Proceso";
  }
  if (type === "flow") {
    return "Flujo";
  }
  if (type === "tag") {
    return "Etiqueta";
  }
  if (type === "expedient_base_entity") {
    return "Entidad base de expediente";
  }
  if (type === "expedient_base_registry") {
    return "Expediente";
  }
  if (type === "user" || type === "applicant") {
    return "Usuario";
  } else {
    return type;
  }
};

export const logMessageValidation = (logData: LogItemType) => {
  if (logData?.action === "deleted") {
    const states = logData?.subject?.user_states || [];
    for (let i = 0; i < states.length; i++) {
      const state = logData.subject?.user_states?.[i];
      if (!state) continue;
      const logDateUTC = new Date(dateToUtc(logData?.date)).getTime();
      const stateDateUTC = new Date(state.created_at).getTime();

      if (logDateUTC === stateDateUTC) {
        return state.description;
      }
    }
  }

  return logData.description;
};

const IMPERSONATION_EVENTS = [
  "impersonation_activated",
  "impersonation_deactivated",
  "impersonation_changed",
] as const;

export const isImpersonationEvent = (event?: string) =>
  !!event &&
  IMPERSONATION_EVENTS.includes(event as (typeof IMPERSONATION_EVENTS)[number]);

export function getChanges(
  oldData: LogAttributesType,
  currentData: LogAttributesType,
  subject_type?: string,
): Change[] {
  if (!oldData || !currentData) return [];

  const allowedKeys: Set<string> = new Set([
    "name",
    "description",
    "file_name",
    "status",
    "tags",
    "active", // Tag-specific field
    "permissions",
  ]);

  const changes: Change[] = [];

  const isRegistry = subject_type === "expedient_base_registry";

  for (const key in oldData) {
    const typedKey = key as keyof LogAttributesType;

    if (!isRegistry && !allowedKeys.has(typedKey)) continue;

    // Ignore internal system fields in registries
    if (
      isRegistry &&
      ["id", "created_at", "updated_at", "created_by", "updated_by"].includes(
        key,
      )
    )
      continue;

    const oldValue = oldData[typedKey];
    const newValue = currentData[typedKey];

    const isDifferent =
      Array.isArray(oldValue) && Array.isArray(newValue)
        ? JSON.stringify(oldValue) !== JSON.stringify(newValue)
        : oldValue !== newValue;

    if (isDifferent) {
      changes.push({
        key: typedKey,
        name: key,
        previous: oldValue,
        current: newValue,
      });
    }
  }

  return changes;
}

export const statusTraducer = (status: Status) => {
  switch (status) {
    case FORM_ASSIGNMENTS_STATUS.SUBMITTED:
      return "Enviado";
    case FORM_ASSIGNMENTS_STATUS.COMPLETED:
      return "Completado";
    case FORM_ASSIGNMENTS_STATUS.IN_PROGRESS:
      return "En progreso";
    case FORM_ASSIGNMENTS_STATUS.CORRECTION:
      return "Revisión";
    case FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION:
      return "Observado";
    case FORM_ASSIGNMENTS_STATUS.ASSIGNED:
      return "Pendiente";
    case FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION:
      return "Rechazado";
    case "ON_CONSTRUCTION":
      return "En construcción";
    case "APPROVED":
      return "Aprobado";
    case "PUBLISHED":
      return "Publicado";
    case "ACTIVE":
      return "Activo";
    case "INACTIVE":
      return "Archivado";
    case "DRAFT":
      return "Borrador";
    case "IN_PROCESS":
      return "En proceso";
    case "FORCE_COMPLETED":
      return "Completado forzosamente";
    default:
      return "-";
  }
};

export const statusColor = (status: string): ActionColors => {
  switch (status) {
    case FORM_ASSIGNMENTS_STATUS.SUBMITTED:
      return "info";
    case FORM_ASSIGNMENTS_STATUS.COMPLETED:
      return "success";
    case FORM_ASSIGNMENTS_STATUS.IN_PROGRESS:
      return "info";
    case FORM_ASSIGNMENTS_STATUS.CORRECTION:
      return "info";
    case FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION:
      return "warning";
    case FORM_ASSIGNMENTS_STATUS.ASSIGNED:
      return "neutral";
    case FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION:
      return "danger";
    case "ON_CONSTRUCTION":
      return "info";
    case "APPROVED":
      return "success";
    case "PUBLISHED":
      return "success";
    case "ACTIVE":
      return "success";
    case "INACTIVE":
      return "warning";
    case "DRAFT":
      return "neutral";
    case "IN_PROCESS":
      return "info";
    case "FORCE_COMPLETED":
      return "success";
    default:
      return "neutral";
  }
};
